import { z } from "zod";

/**
 * The wire shape of one entry from `POST messaging/bulk-messages`.
 *
 * The endpoint answers 200 for the batch as a whole and reports per-message outcomes inside it, so a
 * single response array carries two different shapes. A success is an ordinary message. A failure is
 * not one — there is no saved message behind it — so it carries only which message failed and why.
 *
 * Parsing the whole array as messages is what turned a reportable failure into an unhandled ZodError.
 * The throw discarded `issues` — the only place the server says what actually went wrong — rejected
 * the mutation, and left the client re-saving a batch that could never succeed. Modelling the failure
 * as its own shape is what makes the server's own error message reachable.
 *
 * A server predating that cleanup built the failure entry from the REQUEST instead, so it arrives
 * carrying `created` and `forum.createdDate` at .NET's `default(DateTime)` ("0001-01-01T00:00:00", no
 * timezone), `forum.entityClass` null, and a placeholder forum. Both shapes are read, so a rolling
 * deployment reports the failure either way rather than throwing on the older one.
 */
export const BulkMessageFailureZ = z.object({
  id: z.uuid().nullish(),
  /** Why this one message was not saved, e.g. "Message id is required." */
  issues: z.array(z.string()).nonempty(),
});

export type BulkMessageFailure = z.infer<typeof BulkMessageFailureZ>;

/**
 * A saved message, unread. This schema checks only that the entry is an object. `Message.fromJSONArray`
 * reads each one against the message schema on its own, so one malformed message is left out of the
 * answer instead of failing the whole batch: one bad message once failed a reader's read receipts
 * for a whole conversation (#6592).
 */
export const BulkMessageRowZ = z.looseObject({});

export type BulkMessageRow = z.infer<typeof BulkMessageRowZ>;

/**
 * The envelope of one entry. Failure first, deliberately: a successful entry carries `issues: null`,
 * so it cannot match the failure branch and falls through to the row. A stated failure is therefore
 * always read as a failure, and never left out as an unreadable message.
 */
export const BulkMessageResponseZ = z.union([BulkMessageFailureZ, BulkMessageRowZ]);

export type BulkMessageResponse = z.infer<typeof BulkMessageResponseZ>;

export function isBulkMessageFailure(entry: BulkMessageResponse): entry is BulkMessageFailure {
  const issues = (entry as { issues?: unknown }).issues;
  return Array.isArray(issues) && issues.length > 0;
}

/** The complement of {@link isBulkMessageFailure}: a saved message, still to be read. */
export function isBulkMessageSuccess(entry: BulkMessageResponse): entry is BulkMessageRow {
  return !isBulkMessageFailure(entry);
}

/** Every issue the batch reported, flattened, so a partial failure can be stated in one sentence. */
export function bulkMessageIssues(entries: readonly BulkMessageResponse[]): string[] {
  return entries.filter((entry) => isBulkMessageFailure(entry)).flatMap((entry) => entry.issues);
}
